import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { User, Award, CheckCircle, Clock, XCircle, AlertTriangle, Save } from 'lucide-react';
import { toast } from 'sonner';
import { exigeTripartite, VNM_TRIPARTITE } from '../utils/autonomie';

const ValidationAutonomieEntreprise = ({ user }) => {
  const [stagiaires, setStagiaires] = useState([]);
  const [selectedStagiaire, setSelectedStagiaire] = useState('');
  const [validationsTuteur, setValidationsTuteur] = useState([]);
  const [validationsEntreprise, setValidationsEntreprise] = useState({});
  const [appreciationsEntreprise, setAppreciationsEntreprise] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStagiaires();
  }, []);

  useEffect(() => {
    if (selectedStagiaire) {
      loadValidations();
    }
  }, [selectedStagiaire]);

  const loadStagiaires = async () => {
    try {
      const response = await axios.get('/stagiaires');
      setStagiaires(response.data);
      if (response.data.length > 0) {
        setSelectedStagiaire(response.data[0].user_id);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const loadValidations = async () => {
    try {
      // Charger les validations du tuteur
      const compRes = await axios.get(`/competences/stagiaire/${selectedStagiaire}`);
      // Filtrer uniquement les validations (pas les "Validation du Support") soumises au circuit entreprise
      const validations = compRes.data.filter(c => 
        c.sous_categorie !== 'Validation du Support' && c.statut && exigeTripartite(c.categorie, c.sous_categorie)
      );
      setValidationsTuteur(validations);

      // Charger les validations entreprise existantes
      try {
        const entRes = await axios.get(`/validations-autonomie/stagiaire/${selectedStagiaire}`);
        const entValidations = {};
        const entAppreciations = {};
        entRes.data.forEach(v => {
          const key = `${v.categorie}__${v.sous_categorie}`;
          entValidations[key] = v.valide;
          if (v.appreciation) {
            entAppreciations[key] = v.appreciation;
          }
        });
        setValidationsEntreprise(entValidations);
        setAppreciationsEntreprise(entAppreciations);
      } catch (e) {
        // Pas de validations entreprise encore
      }
    } catch (error) {
    }
  };

  const handleValidateAutonomie = async (categorie, sousCategorie, valide) => {
    const key = `${categorie}__${sousCategorie}`;
    const appreciation = appreciationsEntreprise[key] || '';

    // Motif obligatoire en cas de refus
    if (!valide && !appreciation.trim()) {
      toast.error('Vous devez indiquer un motif de refus dans le champ appréciation');
      return;
    }

    try {
      await axios.post('/validations-autonomie', {
        stagiaire_id: selectedStagiaire,
        categorie,
        sous_categorie: sousCategorie,
        valide,
        appreciation,
        motif: appreciation
      });

      if (valide) {
        setValidationsEntreprise(prev => ({ ...prev, [key]: valide }));
        toast.success(`Autonomie validée pour "${sousCategorie}" - Le stagiaire sera notifié`);
      } else {
        // En cas de refus, la compétence est remise "en_cours" côté backend
        // On recharge les données pour refléter le nouvel état
        toast.info(`Autonomie refusée pour "${sousCategorie}" - Le tuteur pourra réévaluer`);
      }
      
      loadValidations();
    } catch (error) {
      toast.error("Erreur lors de la validation");
    }
  };

  const updateAppreciation = (key, text) => {
    setAppreciationsEntreprise(prev => ({
      ...prev,
      [key]: text
    }));
  };

  const currentStagiaire = stagiaires.find(s => s.user_id === selectedStagiaire);

  // Grouper les validations tuteur par catégorie
  const validationsByCategorie = validationsTuteur.reduce((acc, v) => {
    if (!acc[v.categorie]) acc[v.categorie] = [];
    // Éviter les doublons par sous-catégorie (garder la plus récente)
    const existing = acc[v.categorie].find(x => x.sous_categorie === v.sous_categorie);
    if (!existing || new Date(v.date) > new Date(existing.date)) {
      acc[v.categorie] = acc[v.categorie].filter(x => x.sous_categorie !== v.sous_categorie);
      acc[v.categorie].push(v);
    }
    return acc;
  }, {});

  // Compter les validations en attente (une par sous-catégorie)
  const pendingCount = Object.values(validationsByCategorie).flat().filter(v => {
    const key = `${v.categorie}__${v.sous_categorie}`;
    return v.statut === 'competent' && validationsEntreprise[key] === undefined;
  }).length;

  if (loading) {
    return <div className="text-center py-8">Chargement...</div>;
  }

  return (
    <div className="space-y-6" data-testid="validation-autonomie-entreprise">
      <div>
        <h1 className="text-4xl font-display font-bold text-[#0F172A] mb-2">Validation de l'Autonomie</h1>
        <p className="text-slate-600">Validez l'autonomie de vos stagiaires suite aux évaluations du tuteur</p>
      </div>

      {/* Info Double Validation */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-semibold mb-1">Système de double validation</p>
            <p>Le stagiaire ne reçoit la validation d'autonomie que lorsque le tuteur ET l'entreprise ont tous deux validé la compétence.</p>
            <p className="mt-1" data-testid="info-vnm-tripartite">Pour le VNM, seule la sous-catégorie « {VNM_TRIPARTITE} » demande votre validation ; les autres sont validées par le tuteur seul.</p>
          </div>
        </div>
      </div>

      {/* Sélection Stagiaire */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
          <div className="space-y-2">
            <Label htmlFor="stagiaire">Stagiaire</Label>
            <select
              id="stagiaire"
              value={selectedStagiaire}
              onChange={(e) => setSelectedStagiaire(e.target.value)}
              className="w-full h-10 px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8DC63F]"
            >
              {stagiaires.length === 0 ? (
                <option value="">Aucun stagiaire assigné</option>
              ) : (
                stagiaires.map((s) => (
                  <option key={s.user_id} value={s.user_id}>
                    {s.user_info?.prenom} {s.user_info?.nom}
                  </option>
                ))
              )}
            </select>
          </div>
          {pendingCount > 0 && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
              <p className="text-orange-700 font-medium">
                {pendingCount} validation(s) en attente de votre approbation
              </p>
            </div>
          )}
        </div>

        {currentStagiaire && (
          <div className="mt-4 p-4 bg-slate-50 rounded-lg flex items-center space-x-4">
            <div className="w-12 h-12 bg-[#8DC63F]/10 rounded-full flex items-center justify-center">
              <User className="h-6 w-6 text-[#8DC63F]" />
            </div>
            <div>
              <p className="font-semibold text-[#0F172A]">
                {currentStagiaire.user_info?.prenom} {currentStagiaire.user_info?.nom}
              </p>
              <p className="text-sm text-slate-600">
                {currentStagiaire.centre_formation} - {currentStagiaire.statut}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Validations par Catégorie */}
      {Object.keys(validationsByCategorie).length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm text-center">
          <Award className="h-12 w-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">Aucune évaluation du tuteur à valider pour le moment</p>
        </div>
      ) : (
        Object.entries(validationsByCategorie).map(([categorie, validations]) => (
          <div key={categorie} className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
            <h2 className="text-xl font-display font-semibold text-[#0F172A] mb-4 flex items-center gap-2">
              <Award className="h-5 w-5 text-[#8DC63F]" />
              {categorie}
            </h2>

            <div className="space-y-4">
              {validations.map((validation) => {
                const key = `${validation.categorie}__${validation.sous_categorie}`;
                const entrepriseValidation = validationsEntreprise[key];
                const isTuteurCompetent = validation.statut === 'competent';
                const isFullyValidated = isTuteurCompetent && entrepriseValidation === true;
                const isRefused = entrepriseValidation === false;
                const isPending = isTuteurCompetent && entrepriseValidation === undefined;

                return (
                  <div 
                    key={validation.sous_categorie} 
                    className={`p-4 border rounded-lg ${
                      isFullyValidated ? 'border-green-300 bg-green-50/50' :
                      isRefused ? 'border-red-300 bg-red-50/50' :
                      isPending ? 'border-orange-300 bg-orange-50/50' :
                      'border-slate-200'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-semibold text-[#0F172A]">{validation.sous_categorie}</h3>
                        <p className="text-sm text-slate-600">
                          Évalué le {new Date(validation.date).toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {/* Statut Tuteur */}
                        <div className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          validation.statut === 'competent' ? 'bg-green-100 text-green-700' :
                          validation.statut === 'en_cours' ? 'bg-orange-100 text-orange-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          Tuteur: {validation.statut === 'competent' ? 'Compétent' : 
                                   validation.statut === 'en_cours' ? 'En cours' : 'Non compétent'}
                        </div>
                        {/* Statut Entreprise */}
                        {entrepriseValidation !== undefined && (
                          <div className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            entrepriseValidation ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                          }`}>
                            Entreprise: {entrepriseValidation ? 'Validé' : 'Refusé'}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Appréciation du tuteur */}
                    {validation.appreciation && (
                      <div className="mb-3 p-3 bg-white rounded-lg border-l-4 border-[#8DC63F]">
                        <p className="text-xs font-medium text-slate-600 mb-1">Appréciation du tuteur:</p>
                        <p className="text-sm text-slate-700 italic">"{validation.appreciation}"</p>
                      </div>
                    )}

                    {/* Si tuteur a validé "compétent", montrer les options de validation entreprise */}
                    {isTuteurCompetent && (
                      <div className="space-y-3">
                        {/* Champ appréciation entreprise */}
                        <div>
                          <Label className="text-xs text-slate-600">
                            Votre appréciation <span className="text-red-500">(obligatoire en cas de refus)</span>
                          </Label>
                          <textarea
                            value={appreciationsEntreprise[key] || ''}
                            onChange={(e) => updateAppreciation(key, e.target.value)}
                            placeholder="Ajouter une appréciation ou un motif de refus..."
                            className="w-full h-16 px-3 py-2 text-sm border border-slate-200 rounded-md resize-none mt-1"
                          />
                        </div>

                        {/* Boutons de validation */}
                        <div className="flex items-center gap-3">
                          <Button
                            onClick={() => handleValidateAutonomie(validation.categorie, validation.sous_categorie, true)}
                            className={`flex-1 ${
                              entrepriseValidation === true 
                                ? 'bg-green-600 hover:bg-green-600' 
                                : 'bg-green-500 hover:bg-green-600'
                            } text-white`}
                            disabled={entrepriseValidation === true}
                          >
                            <CheckCircle className="h-4 w-4 mr-2" />
                            {entrepriseValidation === true ? 'Autonomie validée' : "Valider l'autonomie"}
                          </Button>
                          <Button
                            onClick={() => handleValidateAutonomie(validation.categorie, validation.sous_categorie, false)}
                            variant="outline"
                            className={`${
                              entrepriseValidation === false 
                                ? 'border-red-500 text-red-500' 
                                : 'border-red-300 text-red-600 hover:bg-red-50'
                            }`}
                            disabled={entrepriseValidation === false}
                          >
                            <XCircle className="h-4 w-4 mr-2" />
                            Refuser
                          </Button>
                        </div>

                        {/* Statut final */}
                        {isFullyValidated && (
                          <div className="flex items-center gap-2 p-3 bg-green-100 rounded-lg">
                            <CheckCircle className="h-5 w-5 text-green-600" />
                            <span className="text-green-700 font-medium">
                              Double validation complète - Le stagiaire a reçu cette autonomie
                            </span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Si tuteur n'a pas encore validé "compétent" */}
                    {!isTuteurCompetent && (
                      <div className="flex items-center gap-2 p-3 bg-slate-100 rounded-lg">
                        <Clock className="h-5 w-5 text-slate-500" />
                        <span className="text-slate-600 text-sm">
                          En attente de la validation "Compétent" du tuteur
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export default ValidationAutonomieEntreprise;
